/**
 * AppShell — layout matching platform-redesign-v2.jsx exactly:
 *  • User card (avatar + name + role) inside sidebar
 *  • Stats bar (groups / students / today)
 *  • Lesson teaser card (current live lesson)
 *  • Icon-box nav items with left-border active indicator
 *  • Breadcrumb topbar + search + action button
 */
import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { useAppStore } from '@/stores/app.store';
import { useLogout } from '@/hooks/mutations/useAuth';
import { useGroups } from '@/hooks/queries/useGroups';
import { useQuery } from '@tanstack/react-query';
import LessonsAPI from '@/api/lessons.api';
// import WalletAPI from '@/api/wallet.api'; // WALLET HIDDEN
import { ROUTES } from '@/router/routes';
import WordTooltip from '@/features/dictionary/WordTooltip';
import BugReportWidget from '@/features/bug-reports/BugReportWidget';
import NotificationBell from '@/components/ui/NotificationBell';
import { usePrefetchMaterials } from '@/hooks/queries/useMaterials';
import { usePrefetchTrainers } from '@/hooks/queries/usePrefetchTrainers';
import { usePrefetchMarket } from '@/hooks/queries/usePrefetchMarket';
import { usePlatformSettings } from '@/hooks/queries/usePlatformSettings';
import { usePendingPolls } from '@/hooks/queries/usePendingPolls';
import AppLogo from '@/components/ui/AppLogo';
import styles from './layout.module.css';
import { isTeacherRole, isStudentRole, getUserRoleLabel } from '@/lib/permissions';

/* ── Inline SVG icons ── */
function IconHome() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
}
function IconUsers() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
}
function IconBook() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>;
}
function IconFolder() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>;
}
function IconDumbbell() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.4 14.4 9.6 9.6"/><path d="M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.829l-1.768-1.768a2 2 0 1 1 2.828 2.829z"/></svg>;
}
function IconShop() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>;
}
function IconCalendar() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
}
function IconBell() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
}
function IconSettings() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>;
}
function IconLogOut() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>;
}
function IconSun() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></svg>;
}
function IconMoon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>;
}

function IconProfile() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
}

/* ── Nav config ── */
interface NavItem { label: string; path: string; icon: string; badge?: string; }
interface NavSection { title: string; items: NavItem[]; }

const getNavSections = (role: string, vocabularyEnabled = true, pendingPolls = 0): NavSection[] => {
  const newsBadge = pendingPolls > 0 ? String(pendingPolls) : undefined;
  if (!isTeacherRole(role)) {
    return [
      { title: 'Навигация', items: [
        { label: 'Главная',    path: ROUTES.HOME,      icon: '🏠' },
        { label: 'Мои группы', path: ROUTES.GROUPS,    icon: '👥' },
        { label: 'Материалы', path: ROUTES.MATERIALS,  icon: '📚' },
        { label: 'Песни & Тексты', path: ROUTES.SONGS, icon: '🎵' },
        { label: 'Тренажёры', path: ROUTES.TRAINERS,   icon: '🏃' },
        ...(vocabularyEnabled ? [{ label: 'Мой словарь', path: ROUTES.VOCABULARY, icon: '📖' }] : []),
      ]},
      { title: 'Маркет', items: [
        { label: 'Магазин курсов', path: ROUTES.MARKET,      icon: '🛒' },
        { label: 'Мои курсы',     path: ROUTES.MY_COURSES,   icon: '🎓' },
      ]},
      { title: 'Новости', items: [
        { label: 'Новости', path: ROUTES.POSTS, icon: '🔔', badge: newsBadge },
      ]},
    ];
  }
  return [
    { title: 'Главное', items: [
      { label: 'Главная',   path: ROUTES.HOME,     icon: '🏠' },
      { label: 'Ученики',   path: ROUTES.GROUPS,   icon: '👤' },
      { label: 'Расписание',path: ROUTES.SCHEDULE, icon: '📅' },
    ]},
    { title: 'Контент', items: [
      { label: 'Мои курсы',  path: ROUTES.MY_COURSES, icon: '📁' },
      { label: 'Материалы',  path: ROUTES.MATERIALS,  icon: '📚' },
      { label: 'Песни & Тексты', path: ROUTES.SONGS, icon: '🎵' },
      { label: 'Тренажёры',  path: ROUTES.TRAINERS,   icon: '🏃' },
      ...(vocabularyEnabled ? [{ label: 'Мой словарь', path: ROUTES.VOCABULARY, icon: '📖' }] : []),
    ]},
    { title: 'Маркет', items: [
      { label: 'Маркет', path: ROUTES.MARKET, icon: '🛒', badge: '2' },
    ]},
    { title: 'Новости', items: [
      { label: 'Новости', path: ROUTES.POSTS, icon: '🔔', badge: newsBadge },
    ]},
  ];
};

/* ── Page title map ── */
const PAGE_TITLES: Record<string, string> = {
  '/':             'Главная',
  '/groups':       'Ученики',
  '/schedule':     'Расписание',
  '/my-courses':   'Мои курсы',
  '/materials':    'Материалы',
  '/songs':        'Песни & Тексты',
  '/trainers':     'Тренажёры',
  '/market':       'Маркет курсов',
  '/posts':        'Новости',
  '/vocabulary':   'Мой словарь',
  '/settings':     'Настройки',
};

/* ── Next lesson card ── */
function NextLessonCard({ userId }: { userId?: string }) {
  const navigate = useNavigate();
  const { data: groups = [] } = useGroups();
  const myGroups = groups.filter((g: any) => {
    const cat = g.category;
    return cat !== 'archive' && cat !== 'archived' && g.type !== 'archive' && !g.isDeleted;
  });
  const ids = myGroups.map((g: any) => g.id);

  const { data: allLessons = [] } = useQuery({
    queryKey: ['allGroupLessons', ids.join(',')],
    queryFn: async () => {
      const results = await Promise.all(ids.map((id: string) => LessonsAPI.getGroupLessons(id)));
      return results.flatMap((lessons, i) =>
        lessons.map(l => ({ ...l, groupId: ids[i]! }))
      );
    },
    enabled: ids.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  const now = Date.now();
  const next = allLessons
    .filter(l => (l.startTimestamp ?? 0) > now)
    .sort((a, b) => (a.startTimestamp ?? 0) - (b.startTimestamp ?? 0))[0];

  if (!next) return null;

  const grp = myGroups.find((g: any) => g.id === (next as any).groupId);
  const groupName = grp?.name || 'Группа';
  const topicName = next.name;
  const students = (grp?.users ?? []).filter((u: any) => !isTeacherRole(u.userRole));
  const minsUntil = Math.round(((next.startTimestamp ?? 0) - now) / 60000);
  const timeLabel = minsUntil < 60
    ? `Через ${minsUntil} мин`
    : minsUntil < 1440
    ? `Через ${Math.round(minsUntil / 60)} ч`
    : new Date(next.startTimestamp!).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  const timeStr = next.startTimestamp
    ? new Date(next.startTimestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : '';
  const cleanStudentName = (u: any) => {
    const fn = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
    if (fn) return fn;
    if (u.name) return u.name;
    if (u.email) return u.email.split('@')[0] || u.email;
    return 'Студент';
  };
  const studentNamesStr = students.slice(0, 2).map(cleanStudentName).join(', ');

  return (
    <div className={styles.lessonTeaser}>
      <div className={styles.nowBadge}>
        <div className={styles.nowDot} />
        {minsUntil <= 15 ? 'ИДЁТ СЕЙЧАС' : 'БЛИЖАЙШИЙ УРОК'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, marginBottom: 2 }}>
        <div className={styles.ltName}>
          {groupName}{topicName && topicName !== groupName ? ` · ${topicName}` : ''}
        </div>
        <div style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 8,
          background: 'rgba(255,255,255,0.2)', color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {timeLabel}
        </div>
      </div>
      <div className={styles.ltTime}>
        {timeStr}{students.length > 0 ? ` · ${studentNamesStr}` : ''}
        {students.length > 2 ? ` +${students.length - 2}` : ''}
      </div>
      <button
        className={styles.ltBtn}
        onClick={() => navigate(`/groups/${(next as any).groupId}/online`)}
      >
        ▶ Начать урок
      </button>
    </div>
  );
}

/* ── Nav with prefetch ── */
function NavContent({ navSections }: { navSections: NavSection[] }) {
  const prefetchMaterials = usePrefetchMaterials();
  const prefetchTrainers  = usePrefetchTrainers();
  const prefetchMarket    = usePrefetchMarket();
  return (
    <nav className={styles.nav}>
      {navSections.map((section, si) => (
        <div key={si} style={{ marginBottom: 8 }}>
          <div className={styles.navSection}>{section.title}</div>
          {section.items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.active : ''}`
              }
              onMouseEnter={
                item.path === ROUTES.MATERIALS ? prefetchMaterials :
                item.path === ROUTES.TRAINERS  ? prefetchTrainers  :
                item.path === ROUTES.MARKET    ? prefetchMarket    :
                undefined
              }
            >
              <span className={styles.navIcon}>{item.icon}</span>
              <span className={styles.navLabel}>{item.label}</span>
              {item.badge && (
                <span className={styles.navBadge}>{item.badge}</span>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

/* ══════════════════════
   WALLET BADGE — HIDDEN (no backend endpoint yet)
══════════════════════ */
// function WalletBadge({ userId }: { userId?: string }) {
//   const navigate = useNavigate();
//   const [balance, setBalance] = useState<number | null>(null);
//   useEffect(() => {
//     if (!userId) return;
//     WalletAPI.getWallet(userId)
//       .then(w => setBalance(w?.balance ?? 0))
//       .catch(() => {});
//   }, [userId]);
//   if (balance === null || balance === undefined) return null;
//   return (
//     <button onClick={() => navigate(ROUTES.WALLET)} title={`Кошелёк: ${balance.toLocaleString('ru-RU')} ₽`}>
//       <span>💰</span>
//       <span>{balance.toLocaleString('ru-RU')} ₽</span>
//     </button>
//   );
// }

/* ══════════════════════
   COMPONENT
══════════════════════ */
export default function AppShell() {
  const { user, impersonation, exitImpersonation } = useAuthStore();
  const { sidebarCollapsed, toggleSidebar, theme, setTheme } = useAppStore();
  const logout = useLogout();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: groups = [] } = useGroups();
  const isDark = theme === 'dark';

  const userInitials = (user?.name || user?.email || '?')
    .split(' ')
    .slice(0, 2)
    .map((w: string) => w[0]?.toUpperCase())
    .join('');

  const displayName = user?.name || user?.email || 'Пользователь';
  const roleLabel = getUserRoleLabel(user?.userRole);

  const studentCount = groups.reduce((n: number, g: any) => n + (g.users?.length ?? 0), 0);
  const todayCount = 4; // from mock schedule


  const { data: platformSettings } = usePlatformSettings();
  const vocabularyEnabled = platformSettings?.vocabulary?.enabled ?? true;
  const { data: pendingPollsCount = 0 } = usePendingPolls();

  // ── WordTooltip: enabled for student & teacher when trigger != 'off' ──
  // (WordTooltip reads DictSettings internally, so we just pass enabled=true
  //  and let the component's live localStorage listener manage the rest)
  const [dictActive, setDictActive] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('ep_dict_settings') ?? '{}');
      return s.trigger !== 'off';
    } catch { return true; }
  });
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'ep_dict_settings' && e.newValue) {
        try { const s = JSON.parse(e.newValue); setDictActive(s.trigger !== 'off'); } catch {}
      }
      // Legacy key from before settings consolidation
      if (e.key === 'ep_dict_enabled') setDictActive(e.newValue !== 'false');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const navSections = getNavSections(user?.userRole || 'student', vocabularyEnabled, pendingPollsCount);

  return (
    <div className={styles.shell}>

      {/* ══════ SIDEBAR ══════ */}
      <aside className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`}>

        {/* Logo row */}
        <NavLink to={ROUTES.HOME} className={styles.sidebarHeader} style={{ textDecoration: 'none' }}>
          <AppLogo size={34} />
          <span className={styles.sidebarBrand}>Английский Пациент</span>
        </NavLink>

        {/* User card — left side navigates, right side has action buttons */}
        <div className={styles.userCard} onClick={() => navigate(user?.userRole === 'student' ? ROUTES.PROFILE : ROUTES.SETTINGS)}>
          <div className={styles.userAvatar}>{userInitials || '?'}</div>
          <div className={styles.userInfo}>
            <div className={styles.userName}>{displayName}</div>
            <div className={styles.userRole}>{roleLabel}</div>
          </div>
          {/* Action icons — bell+moon */}
          <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            {/* Wallet badge HIDDEN — no backend endpoint yet */}
            {/* {user?.userRole !== 'student' && (
              <WalletBadge userId={user?.id} />
            )} */}
            {/* Bell + theme toggle — bottom row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <NotificationBell />
              <button
                className={styles.themeBtn}
                onClick={e => { e.stopPropagation(); setTheme(isDark ? 'light' : 'dark'); }}
                aria-label={isDark ? 'Светлая тема' : 'Тёмная тема'}
                style={{ transition: 'transform 0.3s ease', transform: isDark ? 'rotate(20deg)' : 'rotate(0deg)' }}
              >
                {isDark ? <IconSun /> : <IconMoon />}
              </button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <NavContent navSections={navSections} />

        {/* Next lesson teaser — teacher only */}
        {user?.userRole !== 'student' && (
          <NextLessonCard userId={user?.id} />
        )}

        {/* Footer */}
        <div className={styles.sidebarFooter}>
          {user?.userRole !== 'student' ? (
            <NavLink
              to={ROUTES.SETTINGS}
              className={({ isActive }) =>
                `${styles.footerItem} ${isActive ? styles.active : ''}`
              }
            >
              <span className={styles.footerIcon}>⚙️</span>
              <span>Настройки</span>
            </NavLink>
          ) : (
            <NavLink
              to={ROUTES.PROFILE}
              className={({ isActive }) =>
                `${styles.footerItem} ${isActive ? styles.active : ''}`
              }
            >
              <span className={styles.footerIcon}>👤</span>
              <span>Профиль</span>
            </NavLink>
          )}
          <button
            className={styles.footerItem}
            onClick={() => logout.mutate()}
          >
            <span className={styles.footerIcon}>🚪</span>
            <span>Выйти</span>
          </button>
        </div>
      </aside>

      {/* ══════ MAIN ══════ */}
      <div className={styles.main}>

        {/* Impersonation banner */}
        {impersonation && (
          <div style={{
            position: 'sticky', top: 0, zIndex: 999,
            background: 'linear-gradient(90deg,#b45309,#d97706)',
            color: '#fff', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', padding: '8px 20px',
            fontSize: 13, fontWeight: 600, gap: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}>
            <span>
              👁️‍🗨️ Вы просматриваете платформу как&nbsp;
              <b>{user?.firstName} {user?.lastName || user?.email}</b>
            </span>
            <button
              onClick={() => { exitImpersonation(); navigate('/admin/users'); }}
              style={{
                padding: '4px 14px', borderRadius: 6, border: '1.5px solid rgba(255,255,255,0.6)',
                background: 'transparent', color: '#fff', cursor: 'pointer',
                fontSize: 12, fontWeight: 700, transition: 'background .15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              ← Выйти из режима просмотра
            </button>
          </div>
        )}

        {/* Page content */}
        <main className={styles.content} data-scroll-container>
          <Outlet />
        </main>
      </div>

      {(user?.userRole === 'student' || isTeacherRole(user?.userRole)) &&
        dictActive && !location.pathname.startsWith('/vocabulary') &&
        <WordTooltip enabled />}

      <BugReportWidget />
    </div>
  );
}
